import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { EffectComposer, RenderPass, EffectPass, OutlineEffect } from 'postprocessing';
import PopupChartFor3d from '../Charts/popupLineChartFor3d';
import { graphFetch } from '../../api/fetchConfig';
import useSystemStatusManager from '../../hook/UseSystemStatusManager';
import useLocalStorageVO from '../Common/UseLocalStorageVO';

const ModelViewer = ({ tabId, modelStatus, fileEntity }) => {
  const mountRef = useRef(null);
  const controlsRef = useRef(null);
  const modelRef = useRef();
  const outlineEffectRef = useRef();
  const hoveredObjRef = useRef(null); // Ref to store hovered object

  // 시스템 상태 관리자와 로그인 사용자 정보 (컴포넌트 최상단으로 이동)
  const { compCdGroup = [], siteGroup = [] } = useSystemStatusManager();
  const { loginUser } = useLocalStorageVO();

  // 회사 이름 추출
  const companyName = compCdGroup.find(c => c.compCd === loginUser.compCd)?.companyName;
  const siteName = siteGroup.find(s => s.codeId === loginUser.site)?.codeName;

  // 팝업 상태
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedDeviceNumber, setSelectedDeviceNumber] = useState(null);
  // IOT 데이터 상태 (LineChartBase와 동일 구조)
  const [data, setData] = useState([]);
  const [lines, setLines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const moveSpeed = 0.1;

  // 키보드 상태 관리를 위한 ref 추가 (클로저 문제 해결)
  const keysRef = useRef({
    w: false,
    a: false,
    s: false,
    d: false,
    ArrowUp: false,
    ArrowLeft: false,
    ArrowDown: false,
    ArrowRight: false,
  });
  
  // 키보드 상태 관리를 위한 상태 추가
  const [keys, setKeys] = useState({
    w: false,
    a: false,
    s: false,
    d: false,
    ArrowUp: false,
    ArrowLeft: false,
    ArrowDown: false,
    ArrowRight: false,
  });

  // 3D 객체 이름과 IOT 장비 정보를 매핑하는 데이터
  // 중요: 여기 key 값('DEVICE_001' 등)이 Blender에서 설정한 객체 이름과 일치해야 합니다.
  // deviceId는 백엔드 API에서 사용하는 숫자 형태로 설정 (1,2,3)
  const deviceDataMap = useMemo(() => ({
    'DEVICE_001': { deviceId: '1', name: '장비 1' },
    'DEVICE_002': { deviceId: '2', name: '장비 2' },
    'DEVICE_003': { deviceId: '3', name: '장비 3' },
  }), []);

  // 1,2,3 버튼에 대한 고정 카메라 위치 매핑
  const buttonPositionMap = useMemo(() => ({
    'DEVICE_001': {
      camera: { x: 2, y: 3, z: 8 },    // 장비 1 시점
      target: { x: 0, y: 1, z: 2 }     // 바라볼 지점
    },
    'DEVICE_002': {
      camera: { x: -3, y: 3, z: 6 },   // 장비 2 시점  
      target: { x: -1, y: 1, z: 0 }    // 바라볼 지점
    },
    'DEVICE_003': {
      camera: { x: 4, y: 3, z: -2 },   // 장비 3 시점
      target: { x: 2, y: 1, z: -3 }    // 바라볼 지점
    }
  }), []);

  // IOT 데이터 fetch 함수 (IotChart와 동일)
  const fetchIotData = () => {
    const query = `
      query getPowerData {
        getPowerData {
          timeLabel
          deviceId
          power
        }
      }
    `;
    graphFetch(query).then((apiResponse) => {
      // 1. API 응답 자체가 없는 경우 처리
      if (!apiResponse) {
        console.error('IOT 데이터 fetch 실패 데이터가 없습니다.');
        return;
      }
      // 2. API 응답 내에 errors 필드가 있는지 확인
      if (apiResponse.errors) {
        console.error('IOT 데이터 fetch 실패 (GraphQL 오류):', apiResponse.errors);
        return;
      }

      // 3. 오류가 없다면 getPowerData 접근
      const result = apiResponse.getPowerData;

      if (result && result.length > 0) {
        const deviceIds = new Set();
        const newDataPoint = {};
        newDataPoint.timeLabel = result[0].timeLabel.replace("T"," ").replace("Z"," ");
        result.forEach(item => {
          deviceIds.add(item.deviceId);
          const raw = parseFloat(item.power);
          newDataPoint[item.deviceId] = Number(raw.toFixed(2));
        });
        setData(prev => {
          const updated = [...prev, newDataPoint];
          if(updated.length > 60){ updated.shift(); }
          return updated;
        });
        const availableColors = ["blue", "deeppink", "green", "orange", "purple", "red"];
        const newLines = Array.from(deviceIds).map((deviceId, idx) => ({
          key: deviceId,
          color: availableColors[idx % availableColors.length]
        }));
        setLines(newLines);
      } else {
        // getPowerData는 존재하지만, 데이터가 비어있는 경우 (오류는 아님)
        console.warn('IOT 데이터는 수신했으나, getPowerData 결과가 비어있거나 null입니다:', result);
      }
    }).catch(error => {
      // graphFetch 자체의 실패 또는 네트워크 오류 등
      console.error('graphFetch 실행 중 오류 발생:', error);
    });
  };

  // 키보드 이벤트 핸들러
  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase();
      if (['w', 'a', 's', 'd'].includes(key) || ['ArrowUp', 'ArrowLeft', 'ArrowDown', 'ArrowRight'].includes(e.key)) {
        e.preventDefault(); // 기본 동작 방지
        const keyToSet = ['ArrowUp', 'ArrowLeft', 'ArrowDown', 'ArrowRight'].includes(e.key) ? e.key : key;
        
        // ref 업데이트
        keysRef.current[keyToSet] = true;
        
        setKeys(prev => ({ ...prev, [keyToSet]: true }));
      }
    };

    const handleKeyUp = (e) => {
      const key = e.key.toLowerCase();
      if (['w', 'a', 's', 'd'].includes(key) || ['ArrowUp', 'ArrowLeft', 'ArrowDown', 'ArrowRight'].includes(e.key)) {
        e.preventDefault(); // 기본 동작 방지
        const keyToSet = ['ArrowUp', 'ArrowLeft', 'ArrowDown', 'ArrowRight'].includes(e.key) ? e.key : key;
        
        // ref 업데이트
        keysRef.current[keyToSet] = false;
        
        setKeys(prev => ({ ...prev, [keyToSet]: false }));
      }
    };

    // 이벤트를 document에 등록 (더 안정적)
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // 컴포넌트 마운트 시 포커스 설정
  useEffect(() => {
    if (mountRef.current) {
      mountRef.current.focus();
    }
  }, []);

  // 포인트로 카메라 이동 함수
  const moveCameraToPoint = (targetPosition) => {
    if (!controlsRef.current) return;
    
    const controls = controlsRef.current;
    const camera = controls.object;
    const startPosition = camera.position.clone();
    const startTarget = controls.target.clone();
    
    // 객체의 중심을 바라보도록 target 설정
    const newTargetPosition = targetPosition.clone();
    
    // 카메라 위치는 객체 앞에서 약간 위를 보도록 설정
    const targetPoint = new THREE.Vector3(
      targetPosition.x,
      targetPosition.y + 1.5,
      targetPosition.z + 3 
    );
    
    let progress = 0;
    const duration = 1000;
    const startTime = Date.now();

    const animateMove = () => {
      const currentTime = Date.now();
      progress = Math.min((currentTime - startTime) / duration, 1);
      
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      
      camera.position.lerpVectors(startPosition, targetPoint, easeProgress);
      controls.target.lerpVectors(startTarget, newTargetPosition, easeProgress);
      
      if (progress < 1) {
        requestAnimationFrame(animateMove);
      } else {
        // 애니메이션 완료 후 최종 위치 강제 설정
        camera.position.copy(targetPoint);
        controls.target.copy(newTargetPosition);
      }
    };
    
    animateMove();
  };

  // 고정 위치로 카메라 이동 함수 (targetObject 없이)
  const moveCameraToPosition = (cameraPos, targetPos, source = 'button') => {
    if (!controlsRef.current) {
      return;
    }

    const controls = controlsRef.current;
    const camera = controls.object;
    const startPosition = camera.position.clone();
    const startTarget = controls.target.clone();
    
    const targetPoint = new THREE.Vector3(cameraPos.x, cameraPos.y, cameraPos.z);
    const newTargetPosition = new THREE.Vector3(targetPos.x, targetPos.y, targetPos.z);

    let progress = 0;
    const duration = 1500;
    const startTime = Date.now();

    const animateMove = () => {
      const currentTime = Date.now();
      progress = Math.min((currentTime - startTime) / duration, 1);
      
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      
      camera.position.lerpVectors(startPosition, targetPoint, easeProgress);
      controls.target.lerpVectors(startTarget, newTargetPosition, easeProgress);
      
      if (progress < 1) {
        requestAnimationFrame(animateMove);
      } else {
        camera.position.copy(targetPoint);
        controls.target.copy(newTargetPosition);

        controls.enabled = false;
        setTimeout(() => {
          controls.enabled = true;
          controls.update();
        }, 100);
      }
    };
    
    animateMove();
  };

  useEffect(() => {
    // 모델 상태가 'COMPLETE'가 아니거나 fileEntity가 없으면 3D 렌더링을 시작하지 않음
    if (modelStatus !== 'COMPLETE' || !fileEntity) {
      setIsLoading(false); // 로딩 상태를 false로 설정하여 로딩 스피너가 계속 돌지 않도록 함
      return;
    }
    
    const modelUrl = `/data/imos/${fileEntity.fileName}.${fileEntity.fileExtension}`;
    setIsLoading(true); // 모델 로드를 시작하므로 로딩 상태 활성화

    const currentMount = mountRef.current;
    if (!currentMount) return;

    let camera, renderer, composer, outlineEffect;
    let animationFrameId;

    // --- 인터랙션 관련 변수 ---
    const interactiveObjects = []; // 상호작용할 객체(Mesh)들을 담을 배열
    const originalColors = new Map(); // 객체의 원래 색상을 저장할 Map

    let raycaster = new THREE.Raycaster();
    let mouse = new THREE.Vector2();
    
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xeeeeee);

    camera = new THREE.PerspectiveCamera(75, currentMount.clientWidth / currentMount.clientHeight, 0.1, 1000);
    camera.position.set(3.88, 0.10, 19.17); // 간략화

    try {
      renderer = new THREE.WebGLRenderer({ antialias: true });
    } catch (error) {
      const errorDiv = document.createElement('div');
      errorDiv.innerHTML = `<h2>WebGL 컨텍스트를 생성할 수 없습니다.</h2>`;
      errorDiv.style.padding = '20px';
      errorDiv.style.color = 'red';
      currentMount.innerHTML = '';
      currentMount.appendChild(errorDiv);
      return;
    }

    renderer.setSize(currentMount.clientWidth, currentMount.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    currentMount.appendChild(renderer.domElement);

    // Post-processing
    composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));

    outlineEffect = new OutlineEffect(scene, camera, {
        edgeStrength: 5,
        edgeGlow: 0.5,
        edgeThickness: 1,
        visibleEdgeColor: 0xffffff,
        hiddenEdgeColor: 0xffffff,
    });
    composer.addPass(new EffectPass(camera, outlineEffect));

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controlsRef.current = controls;

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 10, 7.5);
    scene.add(directionalLight);

    const loader = new GLTFLoader();
    
    loader.load(
      modelUrl, // 동적으로 생성된 모델 경로 사용
      (gltf) => {
        const model = gltf.scene;
        modelRef.current = model; // Store model in ref

        model.traverse((child) => {
          if (child.isMesh) {
            // deviceDataMap에 이름이 있는 객체만 interactiveObjects에 추가
            if (deviceDataMap[child.name]) {
              interactiveObjects.push(child);
              child.userData.deviceInfo = deviceDataMap[child.name];
            }
          }
        });
        scene.add(model);
        setIsLoading(false);
        
        // 모델 로드 후 카메라를 전체 모델을 보는 위치로 설정
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        
        // 모델 전체를 보기 위한 거리 계산
        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = camera.fov * (Math.PI / 180);
        const distance = maxDim / (2 * Math.tan(fov / 2));
        
        // 카메라를 위쪽에서 전체 모델을 내려다보는 위치로 설정 (topView)
        camera.position.set(center.x, center.y + distance * 1.5, center.z);
        camera.lookAt(center);
        
        // OrbitControls 타겟을 모델 중심으로 설정
        if (controlsRef.current) {
          controlsRef.current.target.copy(center);
          controlsRef.current.update();
        }
      },
      undefined,
      (error) => {
        setIsLoading(false);
      }
    );

    // Raycaster for mouse interaction
    raycaster = new THREE.Raycaster();
    mouse = new THREE.Vector2();

    // 클릭 이벤트 핸들러
    const onClick = (event) => {
      try {
        if (!renderer || !renderer.domElement || !camera) return;
        
        // 클릭 시점에 직접 raycasting 수행
        const rect = renderer.domElement.getBoundingClientRect();
        const clickMouse = new THREE.Vector2();
        clickMouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        clickMouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        const clickRaycaster = new THREE.Raycaster();
        clickRaycaster.setFromCamera(clickMouse, camera);
        const intersects = clickRaycaster.intersectObjects(interactiveObjects, true);

        if (intersects.length > 0) {
          const clickedObject = intersects[0].object;

          if (clickedObject.userData && clickedObject.userData.deviceInfo) {
            const deviceInfo = clickedObject.userData.deviceInfo;

            // 객체 위치로 카메라 이동 (먼저 실행)
            const targetPosition = new THREE.Vector3();
            clickedObject.getWorldPosition(targetPosition);

            // 카메라 이동 먼저 실행
            moveCameraToPoint(targetPosition);

            // 팝업은 약간 지연 후 표시 (카메라 이동과 충돌 방지)
            setTimeout(() => {
              setSelectedDeviceNumber(deviceInfo.deviceId);
              setPopupOpen(true);
            }, 100);
          }
        } else {
          console.log('[ModelViewer] ❌ 클릭된 상호작용 객체 없음');
        }
      } catch (error) {
        console.error('[ModelViewer] 클릭 이벤트 처리 중 오류:', error);
      }
    };

    const onMouseMove = (event) => {
      try {
        if (!renderer || !renderer.domElement) return;
        
        // 마우스 위치를 정규화된 장치 좌표로 변환
        const rect = renderer.domElement.getBoundingClientRect();
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    
        raycaster.setFromCamera(mouse, camera);
        const intersects = raycaster.intersectObjects(interactiveObjects, true);
    
        if (intersects.length > 0) {
          // 가장 가까운 객체를 hoveredObjRef로 설정
          const intersected = intersects[0].object;
          if (hoveredObjRef.current !== intersected) {
            hoveredObjRef.current = intersected;
          }
          if (currentMount) {
            currentMount.style.cursor = 'pointer';
          }
        } else {
          // 아무것도 감지되지 않으면 초기화
          if (hoveredObjRef.current) {
            hoveredObjRef.current = null;
          }
          if (currentMount) {
            currentMount.style.cursor = 'default';
          }
        }
      } catch (error) {
        console.error('[ModelViewer] 마우스 이벤트 처리 중 오류:', error);
      }
    };

    if (currentMount) {
      currentMount.addEventListener('click', onClick, false);
      currentMount.addEventListener('mousemove', onMouseMove, false);
    }

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      
      try {
        if (controlsRef.current) {
          const camera = controlsRef.current.object;
          const controls = controlsRef.current;
          
          // 이동 속도 설정
          const moveSpeed = 0.1;
          
          const currentKeys = keysRef.current;
          
          // 직선 이동 처리
          let moved = false;
          
          // 카메라의 현재 방향 벡터들 계산
          const forward = new THREE.Vector3();
          const right = new THREE.Vector3();
          
          // 카메라가 바라보는 방향 (forward)
          camera.getWorldDirection(forward);
          forward.y = 0; // Y축 성분 제거 (수평 이동만)
          forward.normalize();
          
          // 카메라 기준 오른쪽 방향 (right)
          right.crossVectors(forward, new THREE.Vector3(0, 1, 0));
          right.normalize();
          
          // W/↑: 카메라가 바라보는 방향으로 앞으로
          if (currentKeys.w || currentKeys.ArrowUp) {
            const movement = forward.clone().multiplyScalar(moveSpeed);
            camera.position.add(movement);
            controls.target.add(movement);
            moved = true;
          }
          
          // S/↓: 카메라가 바라보는 방향의 반대로 뒤로
          if (currentKeys.s || currentKeys.ArrowDown) {
            const movement = forward.clone().multiplyScalar(-moveSpeed);
            camera.position.add(movement);
            controls.target.add(movement);
            moved = true;
          }
          
          // A/←: 카메라 기준 왼쪽으로
          if (currentKeys.a || currentKeys.ArrowLeft) {
            const movement = right.clone().multiplyScalar(-moveSpeed);
            camera.position.add(movement);
            controls.target.add(movement);
            moved = true;
          }
          
          // D/→: 카메라 기준 오른쪽으로
          if (currentKeys.d || currentKeys.ArrowRight) {
            const movement = right.clone().multiplyScalar(moveSpeed);
            camera.position.add(movement);
            controls.target.add(movement);
            moved = true;
          }
        }
        
        if (controlsRef.current) {
          controlsRef.current.update();
        }

        if (hoveredObjRef.current) {
          outlineEffect.selection.set([hoveredObjRef.current]);
          outlineEffect.visibleEdgeColor.set(0xffffff); // 호버 시 흰색
        } else {
          outlineEffect.selection.set(interactiveObjects); // 기본 상태는 파란색
          outlineEffect.visibleEdgeColor.set(0x87ceeb); // 하늘색
        }

        composer.render();
      } catch (error) {
        console.error('[ModelViewer] 애니메이션 루프 중 오류:', error);
      }
    };
    animate();

    const handleResize = () => {
      if (currentMount) {
        const width = currentMount.clientWidth;
        const height = currentMount.clientHeight;
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
        composer.setSize(width, height);
      }
    };
    window.addEventListener('resize', handleResize);

    // 컴포넌트 언마운트 시 정리
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      currentMount.removeEventListener('click', onClick);
      currentMount.removeEventListener('mousemove', onMouseMove);
      
      document.body.style.cursor = 'default';
      
      if (controlsRef.current) controlsRef.current.dispose();
      
      // 재질, 텍스처 등 모든 리소스 정리
      interactiveObjects.forEach(obj => {
        if (obj.material) obj.material.dispose();
        if (obj.geometry) obj.geometry.dispose();
      });

      if (renderer) renderer.dispose();
      if (currentMount && renderer.domElement.parentNode === currentMount) {
          currentMount.removeChild(renderer.domElement);
      }
    };
  }, [tabId, deviceDataMap, modelStatus, fileEntity]); // fileEntity 의존성 추가

  // 상태에 따른 메시지 렌더링
  if (modelStatus !== 'COMPLETE') {
    const messageMap = {
      EMPTY: '사용하시려면 관리자에게 문의해주세요.',
      PROCESSING: '데이터 처리 진행중입니다. 잠시 후 새로고침 해주세요.',
      null: '모델 상태를 확인 중입니다...',
    };

    const StatusDisplay = ({ children }) => (
      <div style={{
        width: '100%',
        height: 'calc(100vh - 150px)',
        minHeight: '500px',
        border: '1px solid #ccc',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '20px',
        background: '#f8f9fa',
        color: '#343a40',
        fontSize: '1.1rem',
      }}>
        {children}
      </div>
    );
    
    return (
      <StatusDisplay>
        {modelStatus === 'PROCESSING' && (
          <>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #e9ecef',
              borderTop: '3px solid #00bfff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
          </>
        )}
        <span>{messageMap[modelStatus] || '상태 정보를 불러오는 중입니다...'}</span>
      </StatusDisplay>
    );
  }

  return (
    <>
      <div 
        ref={mountRef} 
        tabIndex={0}
        style={{ 
          width: '100%', 
          height: 'calc(100vh - 150px)', 
          minHeight: '500px', 
          border: '1px solid #ccc', 
          position: 'relative',
          outline: 'none' // 포커스 아웃라인 제거
        }}
        onMouseDown={() => mountRef.current?.focus()}
      >
        {isLoading && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(255, 255, 255, 0.9)',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #f3f3f3',
              borderTop: '3px solid #00bfff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
            <span>Loading...</span>
          </div>
        )}
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          background: 'rgba(255, 255, 255, 0.9)', // 흰색 배경으로 변경
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 1000,
          overflow: 'hidden',
          border: '1px solid #eee' // 연한 회색 테두리로 변경
        }}>
          {/* 회사 정보 표시 */}
          <div style={{ padding: '12px 16px' }}>
            <div style={{ 
              fontSize: '16px', 
              fontWeight: 'bold', 
              color: '#01579b', // 기존 글자색 유지
              marginBottom: '4px'
            }}>
              {companyName || '회사 정보 없음'}
            </div>
            {siteName && (
              <div style={{ 
                fontSize: '12px', 
                color: '#0277bd' // 기존 글자색 유지
              }}>
                {siteName}
              </div>
            )}
          </div>
          
          {/* 키보드 조작 안내 */}
          <div style={{ padding: '10px 16px', borderTop: '1px solid #eee', fontSize: '12px', fontFamily: 'monospace', lineHeight: '1.4', color: '#333' }}>
            <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>키보드 조작:</div>
            <div>W/↑:앞으로</div>
            <div>S/↓:뒤로</div>
            <div>A/←:왼쪽으로</div>
            <div>D/→:오른쪽으로</div>
            <div style={{ marginTop: '4px', fontSize: '11px', opacity: '0.7' }}>
              마우스: 시점 조작 및 줌
            </div>
          </div>
        </div>
      </div>
      <PopupChartFor3d
        open={popupOpen}
        onClose={() => {
          setPopupOpen(false);
          setSelectedDeviceNumber(null);
        }}
        deviceNumber={selectedDeviceNumber}
      />
    </>
  );
};

export default ModelViewer;